import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Combobox,
  Divider,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Radio,
  Select,
  Text,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import type { OtherExportType } from '@/services/db/export/mutations';
import { useAttributeNameSearch } from '@/services/db/attribute/queries';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';

const SEARCH_DEBOUNCE_MS = 250;

const ALWAYS_IN_DUPLICATES_EXPORT = ['cio_id', 'id'];

const ALWAYS_IN_DEVICES_EXPORT = [
  'cio_id',
  'id',
  'email',
  'device_token',
  'platform',
  'last_status',
  'last_used',
  'last_used_RFC3339',
];

type AttributeOption = { value: string; label: string };

// `cio_id` is an internal column, never offered as a pick.
function toOptions(attributes: readonly string[], exclude: readonly string[] = []): AttributeOption[] {
  return attributes
    .filter((value) => value !== 'cio_id' && !exclude.includes(value))
    .map((value) => ({ value, label: value }));
}

type DuplicatedAttributePickerProps = {
  workspaceId: number;
  /** Options when there's no search term, and the fallback when the API fails. */
  staticOptions: readonly AttributeOption[];
  selected: string;
  onSelect: (value: string) => void;
  onClear: () => void;
};

/**
 * The "All duplicates of…" combobox: server-side attribute search, falling
 * back to the environment's sent attributes when the attribute API fails.
 */
function DuplicatedAttributePicker({
  workspaceId,
  staticOptions,
  selected,
  onSelect,
  onClear,
}: DuplicatedAttributePickerProps) {
  const [input, setInput] = useState(selected);
  const [debouncedInput] = useDebouncedValue(input, SEARCH_DEBOUNCE_MS);
  const search = useAttributeNameSearch(workspaceId, debouncedInput.trim());

  const options = useMemo(() => {
    if (input.trim() === '' || search.isError) return [...staticOptions];
    return toOptions(search.data ?? []);
  }, [input, search.isError, search.data, staticOptions]);

  const handleSelectionChange = (value: string | null) => {
    if (value == null) {
      setInput('');
      onClear();
      return;
    }
    const option = options.find((candidate) => candidate.value === value);
    if (option != null) {
      setInput(option.label);
      onSelect(option.value);
    }
  };

  return (
    <Combobox
      mt="100"
      ml="200"
      pl="050"
      ariaLabel="Select duplicated attribute"
      inputValue={input}
      onValueChange={setInput}
      selectedValue={selected}
      onSelectionChange={handleSelectionChange}
      options={options}
      isLoading={search.isFetching}
      shouldFilter={false}
      placeholder="Select attribute"
    />
  );
}

type AttributesToExportPickerProps = {
  workspaceId: number;
  /** The pickable set: sent attributes minus the always-included ones. */
  availableAttributes: readonly string[];
  alwaysIncluded: readonly string[];
  selected: readonly string[];
  onChange: (attributes: string[]) => void;
};

/**
 * The "Choose attributes…" multiselect: server-side attribute search (static
 * fallback on API failure) plus the always-included note.
 */
function AttributesToExportPicker({
  workspaceId,
  availableAttributes,
  alwaysIncluded,
  selected,
  onChange,
}: AttributesToExportPickerProps) {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const search = useAttributeNameSearch(workspaceId, debouncedSearch.trim());

  const options = useMemo(() => {
    const base =
      searchInput.trim() === '' || search.isError
        ? toOptions(availableAttributes)
        : toOptions(search.data ?? [], alwaysIncluded);
    // Keep already-selected values rendering as tokens even when the current
    // search page doesn't contain them.
    const extra = selected
      .filter((value) => !base.some((option) => option.value === value))
      .map((value) => ({ value, label: value }));
    return [...base, ...extra];
  }, [searchInput, search.isError, search.data, availableAttributes, alwaysIncluded, selected]);

  return (
    <Box ml="200" mb="400" pl="050">
      <Select
        w="full"
        ariaLabel="Select attributes to export"
        isMulti
        isSearchable
        searchInputValue={searchInput}
        onSearchInput={(event) => {
          if (event.currentTarget instanceof HTMLInputElement) {
            setSearchInput(event.currentTarget.value);
          }
        }}
        placeholder="Select attributes"
        options={options}
        value={[...selected]}
        onChange={(values: string[] | null) => onChange(values ?? [])}
        isLoading={search.isFetching}
      />
      <Paragraph color="subtle" mt="050">
        This export will automatically include the following attributes:{' '}
        <Text weight="semibold">{alwaysIncluded.join(', ')}</Text>
      </Paragraph>
    </Box>
  );
}

export type ExportOtherModalData = {
  workspaceId: number;
  /** The environment's sent attributes: the static option set and defaults. */
  sentAttributes: readonly string[];
  onExport: (exportType: OtherExportType, attributes: string[], duplicatedAttribute: string | null) => void;
};

function alwaysIncludedFor(exportType: OtherExportType, duplicatedSelected: string): string[] {
  if (exportType === 'devices') return ALWAYS_IN_DEVICES_EXPORT;
  return duplicatedSelected === ''
    ? ALWAYS_IN_DUPLICATES_EXPORT
    : [...new Set([...ALWAYS_IN_DUPLICATES_EXPORT, duplicatedSelected])];
}

/**
 * The "Export other" flow: a devices CSV or a duplicated-attribute CSV,
 * with server-side attribute search in both pickers and the environment's
 * sent attributes as the fallback when the attribute API fails. Port of the
 * Ember `people-export-other-modal`.
 */
export const ExportOtherModal = ({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<ExportOtherModalData>) => {
  const { workspaceId, sentAttributes, onExport } = data;
  const { hasPermission } = usePermissions();

  const staticOptions = useMemo(() => toOptions(sentAttributes), [sentAttributes]);
  const envHasAtLeastOneAttribute = staticOptions.length > 0;

  const [exportType, setExportType] = useState<OtherExportType>(
    envHasAtLeastOneAttribute ? 'duplicated_attribute' : 'devices',
  );
  const [duplicatedSelected, setDuplicatedSelected] = useState(() =>
    sentAttributes.includes('email') ? 'email' : (staticOptions[0]?.value ?? ''),
  );
  const [exportAllAttributes, setExportAllAttributes] = useState(true);
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);

  const isExportingDevices = exportType === 'devices';
  const alwaysIncluded = alwaysIncludedFor(exportType, duplicatedSelected);
  const availableAttributes = sentAttributes.filter((value) => !alwaysIncluded.includes(value));

  // Changing the duplicated attribute changes the always-included set, so
  // evict picks that just became automatic.
  const applyDuplicatedSelection = (value: string) => {
    setDuplicatedSelected(value);
    const nextAlways = alwaysIncludedFor('duplicated_attribute', value);
    setSelectedAttributes((current) => current.filter((attribute) => !nextAlways.includes(attribute)));
  };

  const isExportDisabled =
    !hasPermission('journeys/exports/create') ||
    (exportType === 'duplicated_attribute' && duplicatedSelected.trim() === '');

  const handleExport = () => {
    const base = exportAllAttributes ? [...availableAttributes] : [...selectedAttributes];
    const duplicatedAttribute = isExportingDevices || duplicatedSelected === '' ? null : duplicatedSelected;
    const attributes = duplicatedAttribute != null ? [...base, duplicatedAttribute] : base;
    onExport(exportType, attributes, duplicatedAttribute);
    onConfirm();
  };

  return (
    <Modal state={modalState} title="What do you want to export?" onClose={onClose}>
      <ModalBody>
        {envHasAtLeastOneAttribute ? (
          <Box mt="100" mb="200">
            <Radio
              name="export-other-type"
              value="duplicated_attribute"
              label="All duplicates of…"
              isChecked={!isExportingDevices}
              onCheckedChange={() => setExportType('duplicated_attribute')}
            />
            {!isExportingDevices ? (
              <DuplicatedAttributePicker
                workspaceId={workspaceId}
                staticOptions={staticOptions}
                selected={duplicatedSelected}
                onSelect={applyDuplicatedSelection}
                onClear={() => applyDuplicatedSelection('')}
              />
            ) : null}
          </Box>
        ) : null}

        <Box>
          <Radio
            name="export-other-type"
            value="devices"
            label="All devices in this workspace"
            isChecked={isExportingDevices}
            onCheckedChange={() => setExportType('devices')}
          />
        </Box>

        <Divider my="300" />

        <Text display="block" mb="200" text="product-h3">
          Select the attributes you want to include
        </Text>

        <Radio
          name="export-other-attributes"
          value="all"
          label="All attributes"
          mb="200"
          isChecked={exportAllAttributes}
          onCheckedChange={() => setExportAllAttributes(true)}
        />

        <Radio
          name="export-other-attributes"
          value="choose"
          label="Choose attributes…"
          isChecked={!exportAllAttributes}
          onCheckedChange={() => {
            setExportAllAttributes(false);
            // A fresh pick each time: stale selections from a previous visit
            // to this mode would silently join the export.
            setSelectedAttributes([]);
          }}
        />

        {!exportAllAttributes ? (
          // Keyed by the duplicated attribute so a new selection resets the
          // picker's search (mirrors the Ember modal clearing it).
          <AttributesToExportPicker
            key={duplicatedSelected}
            workspaceId={workspaceId}
            availableAttributes={availableAttributes}
            alwaysIncluded={alwaysIncluded}
            selected={selectedAttributes}
            onChange={setSelectedAttributes}
          />
        ) : null}
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" ml="100" isDisabled={isExportDisabled} onClick={handleExport}>
          Export to CSV
        </Button>
      </ModalFooter>
    </Modal>
  );
};
